import { getUserMailboxInfo } from 'owa-client-ids';
import type RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import type ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import type ReadWriteRecipientWellViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientWellViewState';
import getEmailWithRoutingType from 'owa-recipient-email-address/lib/utils/getEmailWithRoutingType';
import {
    lazySearchSuggestionsForSinglePersona,
    lazyLogManuallyAddedContact,
} from 'owa-recipient-suggestions';
import resolvePendingPersonaOnRecipient from './resolvePendingPersonaOnRecipient';
import { action, mutator, orchestrator } from 'satcheljs';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';

/**
 * Action for adding recipients in bulk
 * @param newRecipients New recipients to be added
 * @param recipientWell View state of recipient well
 */
const addRecipientsToRecipientWell = action(
    'addRecipientsToRecipientWell',
    (
        newRecipients: ReadWriteRecipientViewState[],
        recipientWell: ReadWriteRecipientWellViewState
    ) => ({
        newRecipients: newRecipients,
        recipientWell,
    })
);

export default addRecipientsToRecipientWell;

// Mutating recipientWell state
mutator(addRecipientsToRecipientWell, actionMessage => {
    const { recipientWell } = actionMessage;

    recipientWell.recipients.splice(
        recipientWell.recipients.length,
        0,
        ...actionMessage.newRecipients
    );
    recipientWell.isDirty = true;
});

// async fetching recipients for new pending recipients
orchestrator(addRecipientsToRecipientWell, actionMessage => {
    const { newRecipients, recipientWell } = actionMessage;
    newRecipients.forEach(async newRecipient => {
        if (!newRecipient.isPendingResolution) {
            // do nothing for recipients that are not pending.
            return;
        }
        // search by their email address w/ routing type
        const emailWithRouting = getEmailWithRoutingType(newRecipient.persona.EmailAddress);

        let userIdentity = (recipientWell as RecipientWellWithFindControlViewState).userIdentity;

        if (!userIdentity) {
            userIdentity = getUserMailboxInfo().userIdentity;
        }

        const singlePersona = await lazySearchSuggestionsForSinglePersona
            .import()
            .then(searchSuggestionsForSinglePersona =>
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2345 (68,51): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
                // @ts-expect-error
                searchSuggestionsForSinglePersona(userIdentity, emailWithRouting)
            );

        if (isFeatureEnabled('rp-3STelemetryEvents') && singlePersona == null) {
            let recipientWellViewState = recipientWell as RecipientWellWithFindControlViewState;
            const cvid =
                recipientWellViewState.findPeopleFeedbackManager?.conversationId || getGuid();
            lazyLogManuallyAddedContact
                .import()
                .then(logManuallyAddedContact =>
                    logManuallyAddedContact(newRecipient.persona, cvid)
                );
            if (recipientWellViewState.findPeopleFeedbackManager) {
                recipientWellViewState.findPeopleFeedbackManager.conversationId = getGuid();
            }
        }
        resolvePendingPersonaOnRecipient(newRecipient, singlePersona, emailWithRouting);
    });
});
